/**
 * Indicador global de "precisa de ação" no header (Topbar é global → aparece em qualquer
 * tela, inclusive p/ logística que não tem o item Tickets na sidebar):
 *  - bolha persistente com a contagem (some no zero), clica → /tickets filtrado;
 *  - barra full-width no topo quando a contagem AUMENTA (novo pendente), auto-some;
 *  - badge "(N)" no título da aba e toast do SO quando o usuário está FORA da aba.
 *
 * A PERMISSÃO do toast não é pedida aqui: o Topbar é global e o prompt nativo é tiro
 * único, então pedi-la daqui interromperia todo mundo (inclusive quem não usa tickets)
 * sem contexto, e a recusa reflexa seria permanente. O convite vive no painel de
 * Tickets — ver NotificationOptIn.
 *
 * As duas últimas camadas são o item 5 do lote (spec 2026-08-26 §8): a contagem já
 * chegava por polling, faltava alcance com a aba sem foco. A detecção de "aumentou" é
 * uma só (`prev`), compartilhada pela barra e pelo toast — não podem divergir.
 */
import {FC, useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useMyPendingCount} from '../hooks/useMyPendingCount';
import {useDocumentTitleBadge} from '../hooks/useDocumentTitleBadge';
import {isAway, notifyPending} from '../ticketsNotify';

const BANNER_MS = 8000;

const NEEDS_ACTION_URL = '/tickets?needs_action=1';

const NeedsActionHeaderIndicator: FC = () => {
  const count = useMyPendingCount();
  const navigate = useNavigate();
  // Baseline undefined: o 1º valor (load) só firma a referência (sem barra); a barra só
  // dispara quando a contagem AUMENTA depois (novo pendente).
  const prev = useRef<number | undefined>(undefined);
  const [bannerDelta, setBannerDelta] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Barra só quando a contagem aumenta. Sem barra no load (baseline) nem na queda.
  useEffect(() => {
    if (count === undefined) return;
    if (prev.current !== undefined && count > prev.current) {
      const delta = count - prev.current;
      setBannerDelta(delta);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setBannerDelta(null), BANNER_MS);
      // Toast do SO só para quem NÃO está olhando a tela: quem está já vê a barra.
      if (isAway(document.visibilityState, document.hasFocus())) {
        notifyPending(delta, count, () => navigate(NEEDS_ACTION_URL));
      }
    }
    prev.current = count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const n = count ?? 0;
  const dismiss = () => setBannerDelta(null);

  useDocumentTitleBadge(n);

  return (
    <>
      {bannerDelta != null && (
        <div
          role='alert'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '10px 48px',
            background: '#E29400',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            boxShadow: '0 2px 10px rgba(0,0,0,.18)',
          }}
        >
          <i className='bi bi-exclamation-circle' style={{fontSize: 16}} />
          <span>
            {bannerDelta === 1
              ? 'Você tem um novo ticket que precisa da sua ação'
              : `Você tem ${bannerDelta} novos tickets que precisam da sua ação`}
          </span>
          <Link
            to={NEEDS_ACTION_URL}
            onClick={dismiss}
            style={{
              color: '#fff',
              textDecoration: 'underline',
              fontWeight: 700,
            }}
          >
            Ver tickets
          </Link>
          <button
            type='button'
            aria-label='Fechar'
            onClick={dismiss}
            style={{
              position: 'absolute',
              right: 16,
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: 14,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            <i className='bi bi-x-lg' />
          </button>
        </div>
      )}

      {n > 0 && (
        <Link
          to={NEEDS_ACTION_URL}
          className='btn btn-icon btn-active-light-primary w-35px h-35px position-relative ms-1 ms-lg-3'
          title={`${n} ticket(s) precisam da sua ação`}
        >
          <i className='bi bi-ticket-detailed fs-2' />
          <span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'>
            {n > 99 ? '99+' : n}
          </span>
        </Link>
      )}
    </>
  );
};

export {NeedsActionHeaderIndicator};
